import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type PanInfo = { offset: { x: number; y: number }; velocity: { x: number; y: number } };
import { ChevronLeft, ChevronRight, Heart, Quote, Calendar, X, Printer, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Announcement, TemplateType } from '@/types/announcement';
import { getTemplateConfig, TEMPLATE_OPTIONS } from '@/types/templates';
import { RELATIONSHIP_OPTIONS } from '@/types/announcement';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useLanguage } from '@/i18n/LanguageContext';
import { generateMagazinePrintHtml } from '@/lib/printUtils';
import { toast } from 'sonner';

const magCopy = {
  en: {
    printGated: 'Print is available for Connection and Legacy plans',
    pdfGated: 'PDF download is available for Connection and Legacy plans',
    pdfHint: 'Print dialog opened — choose "Save as PDF" to download',
    page: 'Page',
    of: 'of',
    print: 'Print',
    swipeHint: 'Swipe or click corners to flip',
    livingAnnouncements: 'LIVING ANNOUNCEMENTS',
    whileHere: 'While They\'re Still Here',
    livingAnnouncement: 'Living Announcement',
    livingAnnouncementsPlural: 'Living Announcements',
    yourCollection: 'Your Personal Collection',
    legacyCollection: 'Legacy Collection',
    toMyBeloved: 'To my beloved',
  },
  es: {
    printGated: 'Imprimir está disponible para los planes Connection y Legacy',
    pdfGated: 'La descarga de PDF está disponible para los planes Connection y Legacy',
    pdfHint: 'Diálogo de impresión abierto — elige "Guardar como PDF" para descargar',
    page: 'Página',
    of: 'de',
    print: 'Imprimir',
    swipeHint: 'Desliza o haz clic en las esquinas',
    livingAnnouncements: 'ANUNCIOS EN VIDA',
    whileHere: 'Mientras Aún Están Aquí',
    livingAnnouncement: 'Anuncio en Vida',
    livingAnnouncementsPlural: 'Anuncios en Vida',
    yourCollection: 'Tu Colección Personal',
    legacyCollection: 'Colección Legado',
    toMyBeloved: 'A mi querido/a',
  },
  fr: {
    printGated: 'L\'impression est disponible pour les plans Connection et Legacy',
    pdfGated: 'Le téléchargement PDF est disponible pour les plans Connection et Legacy',
    pdfHint: 'Boîte de dialogue d\'impression ouverte — choisissez "Enregistrer en PDF"',
    page: 'Page',
    of: 'sur',
    print: 'Imprimer',
    swipeHint: 'Glissez ou cliquez sur les coins',
    livingAnnouncements: 'ANNONCES VIVANTES',
    whileHere: 'Tant Qu\'Ils Sont Encore Là',
    livingAnnouncement: 'Annonce Vivante',
    livingAnnouncementsPlural: 'Annonces Vivantes',
    yourCollection: 'Votre Collection Personnelle',
    legacyCollection: 'Collection Héritage',
    toMyBeloved: 'À mon cher/ma chère',
  },
  pt: {
    printGated: 'Impressão disponível para planos Connection e Legacy',
    pdfGated: 'Download em PDF disponível para planos Connection e Legacy',
    pdfHint: 'Diálogo de impressão aberto — escolha "Salvar como PDF"',
    page: 'Página',
    of: 'de',
    print: 'Imprimir',
    swipeHint: 'Deslize ou clique nos cantos',
    livingAnnouncements: 'ANÚNCIOS VIVOS',
    whileHere: 'Enquanto Ainda Estão Aqui',
    livingAnnouncement: 'Anúncio Vivo',
    livingAnnouncementsPlural: 'Anúncios Vivos',
    yourCollection: 'Sua Coleção Pessoal',
    legacyCollection: 'Coleção Legado',
    toMyBeloved: 'Ao meu querido/minha querida',
  },
  ar: {
    printGated: 'الطباعة متاحة لخطط Connection و Legacy',
    pdfGated: 'تنزيل PDF متاح لخطط Connection و Legacy',
    pdfHint: 'تم فتح نافذة الطباعة — اختر "حفظ كـ PDF"',
    page: 'صفحة',
    of: 'من',
    print: 'طباعة',
    swipeHint: 'اسحب أو انقر على الزوايا للتقليب',
    livingAnnouncements: 'إعلانات حية',
    whileHere: 'بينما لا يزالون هنا',
    livingAnnouncement: 'إعلان حي',
    livingAnnouncementsPlural: 'إعلانات حية',
    yourCollection: 'مجموعتك الشخصية',
    legacyCollection: 'مجموعة الإرث',
    toMyBeloved: 'إلى حبيبي/حبيبتي',
  },
  ja: {
    printGated: '印刷はConnectionとLegacyプランで利用可能です',
    pdfGated: 'PDFダウンロードはConnectionとLegacyプランで利用可能です',
    pdfHint: '印刷ダイアログが開きました —「PDFとして保存」を選択してください',
    page: 'ページ',
    of: '/',
    print: '印刷',
    swipeHint: 'スワイプまたは角をクリックしてめくる',
    livingAnnouncements: 'リビングアナウンスメント',
    whileHere: 'まだここにいるうちに',
    livingAnnouncement: 'リビングアナウンスメント',
    livingAnnouncementsPlural: 'リビングアナウンスメント',
    yourCollection: 'あなたのコレクション',
    legacyCollection: 'レガシーコレクション',
    toMyBeloved: '愛する',
  },
  ko: {
    printGated: '인쇄는 Connection 및 Legacy 플랜에서 이용 가능합니다',
    pdfGated: 'PDF 다운로드는 Connection 및 Legacy 플랜에서 이용 가능합니다',
    pdfHint: '인쇄 대화 상자가 열렸습니다 — "PDF로 저장"을 선택하세요',
    page: '페이지',
    of: '/',
    print: '인쇄',
    swipeHint: '스와이프하거나 모서리를 클릭하세요',
    livingAnnouncements: '리빙 어나운스먼트',
    whileHere: '아직 여기 있을 때',
    livingAnnouncement: '리빙 어나운스먼트',
    livingAnnouncementsPlural: '리빙 어나운스먼트',
    yourCollection: '나의 컬렉션',
    legacyCollection: '레거시 컬렉션',
    toMyBeloved: '사랑하는',
  },
  id: {
    printGated: 'Cetak tersedia untuk paket Connection dan Legacy',
    pdfGated: 'Unduh PDF tersedia untuk paket Connection dan Legacy',
    pdfHint: 'Dialog cetak terbuka — pilih "Simpan sebagai PDF"',
    page: 'Halaman',
    of: 'dari',
    print: 'Cetak',
    swipeHint: 'Geser atau klik sudut untuk membalik',
    livingAnnouncements: 'LIVING ANNOUNCEMENT',
    whileHere: 'Selagi Mereka Masih Di Sini',
    livingAnnouncement: 'Living Announcement',
    livingAnnouncementsPlural: 'Living Announcement',
    yourCollection: 'Koleksi Pribadi Anda',
    legacyCollection: 'Koleksi Warisan',
    toMyBeloved: 'Untuk tersayang',
  },
};

interface MagazineFlipbookProps {
  announcements: Announcement[];
  onClose: () => void;
}

export function MagazineFlipbook({ announcements, onClose }: MagazineFlipbookProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { credits } = useUserCredits();
  const { language } = useLanguage();
  const mc = magCopy[language] || magCopy.en;
  
  // Check plan access for print/PDF
  const highestBundle = credits?.highest_bundle_purchased || null;
  const hasConnectionAccess = highestBundle === 'connection' || highestBundle === 'legacy';
  const hasLegacyAccess = highestBundle === 'legacy';

  // Total pages = cover + announcements
  const totalPages = announcements.length + 1;

  const goToPage = (page: number, dir: number) => {
    if (isFlipping || page < 0 || page >= totalPages) return;
    setIsFlipping(true);
    setDirection(dir);
    setCurrentPage(page);
    setTimeout(() => setIsFlipping(false), 500);
  };

  const nextPage = () => goToPage(currentPage + 1, 1);
  const prevPage = () => goToPage(currentPage - 1, -1);

  const handleDragEnd = (_: any, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x < -threshold) {
      nextPage();
    } else if (info.offset.x > threshold) {
      prevPage();
    }
  };

  const handleCornerClick = (side: 'left' | 'right') => {
    if (side === 'left') prevPage();
    else nextPage();
  };
  
  const handlePrintMagazine = () => {
    if (!hasConnectionAccess) {
      toast.error(mc.printGated);
      return;
    }
    
    const printContent = generateMagazinePrintHtml(announcements, hasLegacyAccess, language);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };
  
  const handleDownloadMagazinePdf = () => {
    if (!hasConnectionAccess) {
      toast.error(mc.pdfGated);
      return;
    }
    
    const printContent = generateMagazinePrintHtml(announcements, hasLegacyAccess, language);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      toast.success(mc.pdfHint);
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const pageVariants = {
    enter: (dir: number) => ({
      rotateY: dir > 0 ? -90 : 90,
      opacity: 0,
    }),
    center: {
      rotateY: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      rotateY: dir > 0 ? 90 : -90,
      opacity: 0,
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-magazine/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="relative w-full max-w-4xl aspect-[4/3] perspective-1000"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Bar: Page counter, Print/PDF, Close */}
        <div className="absolute -top-12 left-0 right-0 flex items-center justify-between">
          {/* Page Counter */}
          <div className="text-magazine-text/70 text-sm">
            {mc.page} {currentPage + 1} {mc.of} {totalPages}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {hasConnectionAccess && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-magazine-text hover:bg-magazine-text/20 gap-2"
                  onClick={handlePrintMagazine}
                  title="Print all announcements"
                >
                  <Printer className="h-4 w-4" />
                  <span className="hidden sm:inline">{mc.print}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-magazine-text hover:bg-magazine-text/20 gap-2"
                  onClick={handleDownloadMagazinePdf}
                  title="Save as PDF"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-magazine-text hover:bg-magazine-text/20"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Magazine Container */}
        <div className="relative w-full h-full rounded-lg overflow-hidden shadow-2xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
              className="absolute inset-0 cursor-grab active:cursor-grabbing"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {currentPage === 0 ? (
                <CoverPage announcements={announcements} />
              ) : (
                <AnnouncementPage announcement={announcements[currentPage - 1]} pageNumber={currentPage} />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Corner Click Zones */}
          {currentPage > 0 && (
            <button
              className="absolute left-0 top-0 w-1/4 h-full z-20 cursor-pointer group"
              onClick={() => handleCornerClick('left')}
              aria-label="Previous page"
            >
              <div className="absolute bottom-4 left-4 w-12 h-16 border-l-2 border-b-2 border-magazine-text/30 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
          {currentPage < totalPages - 1 && (
            <button
              className="absolute right-0 top-0 w-1/4 h-full z-20 cursor-pointer group"
              onClick={() => handleCornerClick('right')}
              aria-label="Next page"
            >
              <div className="absolute bottom-4 right-4 w-12 h-16 border-r-2 border-b-2 border-magazine-text/30 rounded-br-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
        </div>

        {/* Arrow Navigation */}
        <div className="absolute -left-16 top-1/2 -translate-y-1/2">
          <Button
            variant="ghost"
            size="icon"
            className="text-magazine-text hover:bg-magazine-text/20 disabled:opacity-30"
            onClick={prevPage}
            disabled={currentPage === 0 || isFlipping}
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
        </div>
        <div className="absolute -right-16 top-1/2 -translate-y-1/2">
          <Button
            variant="ghost"
            size="icon"
            className="text-magazine-text hover:bg-magazine-text/20 disabled:opacity-30"
            onClick={nextPage}
            disabled={currentPage === totalPages - 1 || isFlipping}
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        </div>

        {/* Page Dots */}
        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex gap-2">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              className={cn(
                'w-2 h-2 rounded-full transition-all',
                currentPage === i ? 'bg-magazine-text w-6' : 'bg-magazine-text/40 hover:bg-magazine-text/60'
              )}
              onClick={() => goToPage(i, i > currentPage ? 1 : -1)}
            />
          ))}
        </div>

        {/* Swipe Hint */}
        <div className="absolute -bottom-10 right-0 text-magazine-text-muted/50 text-xs">
          {mc.swipeHint}
        </div>
      </div>
    </motion.div>
  );
}

function CoverPage({ announcements }: { announcements: Announcement[] }) {
  const { language } = useLanguage();
  const mc = magCopy[language] || magCopy.en;

  return (
    <div className="w-full h-full bg-gradient-to-br from-magazine via-magazine-accent to-magazine relative overflow-hidden">
      {/* Magazine Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:20px_20px]" />
      </div>

      {/* Cover Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-8 text-magazine-text">
        {/* Magazine Header */}
        <div className="absolute top-6 left-6 right-6 flex justify-between items-center">
          <span className="text-xs tracking-[0.3em] font-light">{mc.livingAnnouncements}</span>
          <span className="text-xs opacity-60">{new Date().toLocaleDateString(language, { month: 'long', year: 'numeric' })}</span>
        </div>

        {/* Main Title */}
        <div className="text-center space-y-4">
          <Heart className="h-12 w-12 mx-auto text-primary" fill="currentColor" />
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight">Say It NOW</h1>
          <p className="text-lg tracking-[0.2em] font-light text-magazine-text/80">{mc.whileHere}</p>
        </div>

        {/* Featured Count */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-magazine-text/20 bg-magazine-text/5">
            <span className="text-3xl font-bold">{announcements.length}</span>
            <span className="text-sm text-magazine-text/70">
              {announcements.length === 1 ? mc.livingAnnouncement : mc.livingAnnouncementsPlural}
            </span>
          </div>
        </div>

        {/* Photo Collage Preview */}
        {announcements.length > 0 && (
          <div className="mt-8 flex justify-center -space-x-4">
            {announcements.slice(0, 4).map((ann, i) => (
              ann.photo_urls?.[0] && (
                <div
                  key={ann.id}
                  className="w-16 h-16 rounded-full overflow-hidden border-2 border-magazine shadow-lg"
                  style={{ zIndex: 4 - i }}
                >
                  <img src={ann.photo_urls[0]} alt="" className="w-full h-full object-cover" />
                </div>
              )
            ))}
          </div>
        )}

        {/* Magazine Footer */}
        <div className="absolute bottom-6 left-6 right-6 flex justify-between items-center text-xs text-magazine-text/50">
          <span>{mc.yourCollection}</span>
          <span>{mc.legacyCollection}</span>
        </div>
      </div>
    </div>
  );
}

function AnnouncementPage({ announcement, pageNumber }: { announcement: Announcement; pageNumber: number }) {
  const { language } = useLanguage();
  const mc = magCopy[language] || magCopy.en;
  const template = getTemplateConfig(announcement.template);
  const relationship = RELATIONSHIP_OPTIONS.find((r) => r.value === announcement.relationship);
  const isHeadline = announcement.template === 'love';

  return (
    <div className={cn('w-full h-full relative overflow-hidden', template.colors.background)}>
      {/* Magazine Page Layout */}
      <div className="h-full flex flex-col md:flex-row">
        {/* Photo Section */}
        <div className="h-1/2 md:h-full md:w-1/2 relative">
          {announcement.photo_urls?.[0] ? (
            <>
              <img
                src={announcement.photo_urls[0]}
                alt={announcement.recipient_name}
                className="w-full h-full object-cover"
              />
              <div className={cn(
                'absolute inset-0 bg-gradient-to-r opacity-40',
                isHeadline ? 'from-magazine/80 to-transparent' : 'from-magazine-text/40 to-transparent'
              )} />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className={cn('h-24 w-24 opacity-20', template.colors.text)} />
            </div>
          )}

          {/* Page Number */}
          <div className={cn(
            'absolute bottom-4 left-4 text-xs font-medium opacity-50',
            template.colors.text
          )}>
            {mc.page} {pageNumber}
          </div>
        </div>

        {/* Content Section */}
        <div className="h-1/2 md:h-full md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className={cn(
              'text-xs tracking-[0.2em] uppercase font-medium mb-2 opacity-60',
              template.colors.text
            )}>
              {relationship?.emoji} {mc.toMyBeloved} {relationship?.label.toLowerCase()}
            </div>
            <h2 className={cn(
              'text-3xl md:text-4xl font-bold',
              template.colors.text
            )}>
              {announcement.recipient_name}
            </h2>
          </div>

          {/* Message */}
          <div className="relative flex-1 flex items-center">
            <Quote className={cn(
              'absolute -left-2 -top-2 h-8 w-8 opacity-20',
              template.colors.text
            )} />
            <p className={cn(
              'text-base md:text-lg leading-relaxed pl-4',
              template.typography.bodyFont,
              template.colors.text,
              'opacity-90'
            )}>
              "{announcement.message}"
            </p>
          </div>

          {/* Footer */}
          <div className={cn(
            'mt-6 pt-4 border-t flex items-center justify-between text-xs opacity-50',
            isHeadline ? 'border-magazine-text/20' : 'border-foreground/10',
            template.colors.text
          )}>
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              {new Date(announcement.created_at).toLocaleDateString(language, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
            <div className="flex items-center gap-1">
              <Heart className="h-3 w-3" fill="currentColor" />
              Say It NOW
            </div>
          </div>
        </div>
      </div>

      {/* Additional Photos */}
      {announcement.photo_urls && announcement.photo_urls.length > 1 && (
        <div className="absolute bottom-4 right-4 flex gap-2">
          {announcement.photo_urls.slice(1, 3).map((url, i) => (
            <div key={i} className="w-12 h-12 rounded-lg overflow-hidden border-2 border-magazine-text/50 shadow-lg">
              <img src={url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
